import { useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { 
  Loader2, 
  RefreshCw, 
  Trash2, 
  AlertTriangle,
  CheckCircle,
  Wallet,
  Banknote,
  Receipt,
  CreditCard,
  Building2,
  Landmark,
  FileText,
  FileCheck,
  Search,
  ShieldAlert,
  XCircle
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

const iconMap = {
  wallet: Wallet,
  banknote: Banknote,
  receipt: Receipt,
  'credit-card': CreditCard,
  building: Building2,
  landmark: Landmark,
  'file-text': FileText,
  'file-check': FileCheck,
};

const OrphanedEntries = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [fixing, setFixing] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, entry: null, category: null });
  const [fixAllDialog, setFixAllDialog] = useState({ open: false, category: null });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const scanOrphaned = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orphaned/scan-all`, { headers });
      setScanResult(response.data);
      
      if (response.data.total_orphaned === 0) {
        toast.success('No orphaned entries found! Your data is clean.');
      } else {
        toast.warning(`Found ${response.data.total_orphaned} orphaned entries totaling ${formatCurrency(response.data.total_amount)}`);
      }
    } catch (error) {
      console.error('Scan failed:', error);
      toast.error('Failed to scan for orphaned entries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fixEntry = async (entryType, entryId) => {
    setFixing(prev => ({ ...prev, [entryId]: true }));
    try {
      const response = await axios.post(
        `${API}/orphaned/fix/${entryType}/${entryId}?action=delete`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      // Re-scan to update the list
      await scanOrphaned();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix entry');
    } finally {
      setFixing(prev => ({ ...prev, [entryId]: false }));
      setConfirmDialog({ open: false, entry: null, category: null });
    }
  };

  const fixAllInCategory = async (categoryName) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/orphaned/fix-all/${categoryName.replace(/ /g, '_')}`,
        {},
        { headers }
      );
      toast.success(`Fixed ${response.data.fixed_count} entries, total amount: ${formatCurrency(response.data.total_amount_affected)}`);
      // Re-scan to update the list
      await scanOrphaned();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix category');
    } finally {
      setLoading(false);
      setFixAllDialog({ open: false, category: null });
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || FileText;
    return Icon;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <ShieldAlert className="w-7 h-7 text-orange-600" />
            Orphaned Entries
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Scan and fix orphaned/broken data references across all categories
          </p>
        </div>
        <Button 
          onClick={scanOrphaned} 
          disabled={loading}
          size="lg"
          className="bg-orange-600 hover:bg-orange-700"
          data-testid="scan-orphaned-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Search className="w-5 h-5 mr-2" />
          )}
          {loading ? 'Scanning...' : 'Scan All Categories'}
        </Button>
      </div>

      {/* Results */}
      {!scanResult && !loading && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="py-16 text-center">
            <ShieldAlert className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Scan Results</h3>
            <p className="text-gray-500 mb-4">
              Click "Scan All Categories" to check for orphaned entries across the system
            </p>
            <p className="text-sm text-gray-400">
              This will scan: Salaries, Advances, Deductions, Bank Transactions, Loans, Liabilities, and more
            </p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-orange-500 animate-spin mb-4" />
            <h3 className="text-lg font-medium text-gray-600">Scanning all categories...</h3>
            <p className="text-gray-500">This may take a moment</p>
          </CardContent>
        </Card>
      )}

      {scanResult && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className={`border-2 ${scanResult.total_orphaned > 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Orphaned</p>
                    <p className={`text-3xl font-bold ${scanResult.total_orphaned > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {scanResult.total_orphaned}
                    </p>
                  </div>
                  {scanResult.total_orphaned > 0 ? (
                    <AlertTriangle className="w-10 h-10 text-red-400" />
                  ) : (
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-orange-200 bg-orange-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Amount Affected</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {formatCurrency(scanResult.total_amount)}
                    </p>
                  </div>
                  <Wallet className="w-10 h-10 text-orange-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Categories Affected</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {scanResult.categories.length}
                    </p>
                  </div>
                  <FileText className="w-10 h-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category-wise Results */}
          {scanResult.total_orphaned === 0 ? (
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-green-700 mb-2">All Clean!</h3>
                <p className="text-green-600">
                  No orphaned entries found. Your data integrity is good.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Scanned at: {new Date(scanResult.scan_time).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {scanResult.categories.map((category) => {
                const Icon = getIcon(category.icon);
                return (
                  <Card key={category.name} className="border-2 border-red-100">
                    <CardHeader className="bg-red-50/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Icon className="w-5 h-5 text-red-600" />
                          {category.name}
                          <Badge variant="destructive" className="ml-2">
                            {category.count} orphaned
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-4">
                          <span className="text-red-600 font-semibold">
                            {formatCurrency(category.total_amount)}
                          </span>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => setFixAllDialog({ open: true, category: category })}
                            data-testid={`fix-all-${category.name}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Fix All ({category.count})
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-center w-24">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {category.entries.map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell>
                                <div>
                                  <span className="font-medium">{entry.description}</span>
                                  {entry.employee_name && !entry.description.includes(entry.employee_name) && (
                                    <span className="text-gray-500 text-sm block">{entry.employee_name}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-red-600">
                                {formatCurrency(entry.amount)}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setConfirmDialog({ open: true, entry, category })}
                                  disabled={fixing[entry.id]}
                                  data-testid={`fix-${entry.id}`}
                                >
                                  {fixing[entry.id] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4" />
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Confirm Single Fix Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Fix Orphaned Entry?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.entry && (
                <div className="mt-2 p-3 bg-red-50 rounded-lg">
                  <p className="font-medium text-gray-900">{confirmDialog.entry.description}</p>
                  <p className="text-red-600 font-semibold mt-1">Amount: {formatCurrency(confirmDialog.entry.amount)}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {confirmDialog.entry.type === 'salary' || confirmDialog.entry.type === 'advance' 
                      ? 'This will reset the amount to ₹0.'
                      : 'This will permanently delete this orphaned record.'}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => confirmDialog.entry && fixEntry(confirmDialog.entry.type, confirmDialog.entry.id)}
            >
              {confirmDialog.entry?.type === 'salary' || confirmDialog.entry?.type === 'advance' 
                ? 'Reset to ₹0'
                : 'Delete Entry'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Fix All Dialog */}
      <AlertDialog open={fixAllDialog.open} onOpenChange={(open) => setFixAllDialog({ ...fixAllDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Fix ALL Orphaned Entries in Category?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {fixAllDialog.category && (
                <div className="mt-2 p-3 bg-red-50 rounded-lg">
                  <p className="font-medium text-gray-900">{fixAllDialog.category.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    This will fix <span className="font-bold text-red-600">{fixAllDialog.category.count}</span> orphaned entries
                  </p>
                  <p className="text-red-600 font-semibold mt-1">
                    Total Amount: {formatCurrency(fixAllDialog.category.total_amount)}
                  </p>
                  <p className="text-sm text-red-500 mt-3 font-medium">
                    ⚠️ This action cannot be undone!
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => fixAllDialog.category && fixAllInCategory(fixAllDialog.category.name)}
            >
              Fix All {fixAllDialog.category?.count} Entries
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrphanedEntries;
